import Alpine from "alpinejs";

const MENU_SELECTOR = ".navigation__menu";
const TAB_FOCUSABLE = ".navigation__tab-button, .navigation__tab--no-items > a";
const SUB_ITEM_SELECTOR = ".navigation__item";

export function init() {
  document.querySelectorAll(MENU_SELECTOR).forEach(initMenu);
}

function initMenu(menuEl) {
  if (menuEl.dataset.navKeyboardInit === "true") return;
  menuEl.dataset.navKeyboardInit = "true";

  menuEl.addEventListener("keydown", (event) => onKeydown(event, menuEl));
  menuEl.addEventListener("focusin", (event) => onFocusin(event, menuEl));
}

function onKeydown(event, menuEl) {
  if (isEditableTarget(event.target)) return;

  const inPanel = event.target.closest(".navigation__items") !== null;
  if (inPanel) {
    handlePanelKey(event, menuEl);
    return;
  }

  const tabFocus = event.target.closest(TAB_FOCUSABLE);
  if (tabFocus) handleTabBarKey(event, menuEl, tabFocus);
}

function handleTabBarKey(event, menuEl, tabFocusEl) {
  const tabContainer = tabFocusEl.closest(".navigation__tab");
  const containers = getTabContainers(menuEl);
  const idx = containers.indexOf(tabContainer);
  if (idx === -1) return;

  switch (event.code) {
    case "ArrowLeft":
      event.preventDefault();
      focusTab(containers[(idx - 1 + containers.length) % containers.length]);
      break;
    case "ArrowRight":
      event.preventDefault();
      focusTab(containers[(idx + 1) % containers.length]);
      break;
    case "Home":
      event.preventDefault();
      focusTab(containers[0]);
      break;
    case "End":
      event.preventDefault();
      focusTab(containers[containers.length - 1]);
      break;
    case "ArrowDown":
      openPanelAndFocusFirst(event, menuEl, tabContainer, tabFocusEl);
      break;
    case "Escape":
      closeIfOpen(event, menuEl);
      break;
  }
}

function handlePanelKey(event, menuEl) {
  const panelEl = event.target.closest(".navigation__items");
  const tabContainer = panelEl.closest(".navigation__tab");
  const subItems = [...panelEl.querySelectorAll(SUB_ITEM_SELECTOR)];
  const idx = subItems.indexOf(event.target);

  switch (event.code) {
    case "ArrowLeft":
      if (subItems.length === 0) return;
      event.preventDefault();
      subItems[(idx - 1 + subItems.length) % subItems.length].focus();
      break;
    case "ArrowRight":
      if (subItems.length === 0) return;
      event.preventDefault();
      subItems[(idx + 1) % subItems.length].focus();
      break;
    case "Home":
      if (subItems.length === 0) return;
      event.preventDefault();
      subItems[0].focus();
      break;
    case "End":
      if (subItems.length === 0) return;
      event.preventDefault();
      subItems[subItems.length - 1].focus();
      break;
    case "ArrowUp":
    case "Escape":
      event.preventDefault();
      closePanelAndFocusTab(menuEl, tabContainer);
      break;
  }
}

function openPanelAndFocusFirst(event, menuEl, tabContainer, tabFocusEl) {
  const panelEl = tabContainer.querySelector(":scope > .navigation__items");
  if (!panelEl) return;
  event.preventDefault();
  // Alpine click handler sets activeTabId on the menu scope, opening the panel.
  tabFocusEl.click();
  requestAnimationFrame(() => {
    panelEl.querySelector(SUB_ITEM_SELECTOR)?.focus();
  });
}

function closeIfOpen(event, menuEl) {
  const data = Alpine.$data(menuEl);
  if (data.activeTabId && data.activeTabId !== data.currentPageTabId) {
    event.preventDefault();
    data.activeTabId = data.currentPageTabId;
  }
}

function closePanelAndFocusTab(menuEl, tabContainer) {
  const data = Alpine.$data(menuEl);
  data.activeTabId = data.currentPageTabId;
  requestAnimationFrame(() => {
    tabContainer.querySelector(TAB_FOCUSABLE)?.focus();
  });
}

/**
 * When focus enters a tab whose panel is not the open one, close the previously open panel.
 * Avoids leaving an orphan open popover while the user keyboard-tabs to another section.
 *
 * @param {FocusEvent} event
 * @param {HTMLElement} menuEl
 */
function onFocusin(event, menuEl) {
  const tabContainer = event.target.closest(".navigation__tab");
  if (!tabContainer || !menuEl.contains(tabContainer)) return;
  // Only react when focus is on a top-level tab (not on a sub-item inside the panel).
  if (event.target.closest(".navigation__items")) return;

  const tabData = Alpine.$data(tabContainer);
  const menuData = Alpine.$data(menuEl);
  if (!tabData?.tabId) return;
  // When focus jumps to a different tab, revert to the current-page panel — but leave
  // it visible if it was already open. The current-page panel acts as the resting state.
  if (menuData.activeTabId && menuData.activeTabId !== tabData.tabId && menuData.activeTabId !== menuData.currentPageTabId) {
    menuData.activeTabId = menuData.currentPageTabId;
  }
}

function getTabContainers(menuEl) {
  return [...menuEl.querySelectorAll(":scope > .navigation__tab")];
}

function focusTab(tabContainer) {
  tabContainer?.querySelector(TAB_FOCUSABLE)?.focus();
}

function isEditableTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}
